"use client";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOrg } from "@/hooks/useOrg";
import { PhoneScript } from "@/types/campaigns";
import { UseCase } from "@/types/scripts";
import { apiLive, apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";
import { createMinDurationEnforcer } from "@/utils/onboarding/common";
import { putOnboardingPhoneScript } from "@/utils/onboarding/onboardingScriptApi";
import { scriptApiVersionFromFeatures } from "@/utils/org/scriptsApiVersion";
import { getScriptDetailUrl, type ScriptApiVersion } from "@/utils/scriptVersioning/api";
import {
  associationsFromEmbeddedUseCases,
  cloneUseCaseForImport,
  hydrateUseCasesFromScript,
  shouldSkipUseCaseBatchFetch,
} from "@/utils/scripts/embeddedUseCases";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { ScriptBuildingLoader } from "./ScriptBuildingLoader";
import { ScriptPreviewSection } from "./ScriptPreviewSection";
import { StepErrorState } from "./StepErrorState";
import { StepFooter } from "./StepFooter";

interface InboundScriptStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
  /** Use-case IDs created during directory setup that should be linked to the script. */
  readonly newUseCaseIds?: string[];
  /** Called after newUseCaseIds have been consumed so the parent can clear them. */
  readonly onUseCaseIdsConsumed?: () => void;
}

type InboundRulesSimple = {
  defaultScriptId: string;
  rules: Array<{
    scriptId: string;
    timeFrame: string;
    hours: string;
    phoneNumber?: string;
  }>;
};

type ScriptGenerationResponse = {
  success: boolean;
  scriptId?: string;
  script?: PhoneScript;
  useCases?: UseCase[];
};

type GenerationStatus = { state: "generating" } | { state: "ready" } | { state: "error" };

const SCRIPT_BUILD_WORKSTREAM = [
  // Phase 1 – Greeting
  "initGreetingTemplate({ tone: 'warm' })",
  "setBrandVoice('trusted_professional')",
  "mapOrganizationName(config.org)",
  // Phase 2 – Data scan
  "scanBusinessData(hours, services, faqs)",
  "indexTransferRules(directory)",
  "resolveTimezones(schedule)",
  // Phase 3 – Use cases
  "composeUseCases(['transfer', 'voicemail'])",
  "linkDirectoryContacts(useCases)",
  "buildDecisionTree(flows)",
  // Phase 4 – Compliance
  "runPolicyChecks({ compliance: true })",
  "validateSafeLanguage(scriptDraft)",
  "auditCallFlow(decisionTree)",
  // Phase 5 – Export
  "finalizeScriptBundle()",
  "exportScriptBundle({ format: 'v1' })",
  "registerActivationHook(bundle)",
];
const SCRIPT_FETCH_RETRY_ATTEMPTS = 4;
const MIN_BUILDING_SCRIPT_LOADING_MS = 20_000;

/**
 * Associate newly-created use-case IDs with a script (if not already linked).
 * Returns the updated script on success, or the original script if nothing changed.
 */
async function associateNewUseCases(
  script: PhoneScript,
  newIds: string[],
  version: ScriptApiVersion
): Promise<PhoneScript> {
  const existing = new Set(script.useCaseAssociations?.map((a) => a.id) ?? []);
  const toAdd = newIds.filter((id) => !existing.has(id));
  if (toAdd.length === 0) return script;

  if (version === 2) {
    const imported = await Promise.all(
      toAdd.map((id) => apiLive<UseCase>(`${GAIL_API}/v1/use_cases/${id}`))
    );
    const cloned = imported
      .filter((useCase): useCase is UseCase => Boolean(useCase))
      .map(cloneUseCaseForImport);
    const useCases = [...(script.useCases ?? []), ...cloned];
    const updatedScript = {
      ...script,
      useCases,
      useCaseAssociations: associationsFromEmbeddedUseCases(useCases),
    };
    const response = await putOnboardingPhoneScript(updatedScript, 2);
    return response ?? updatedScript;
  }

  const startOrder = (script.useCaseAssociations?.length ?? 0) + 1;
  const newAssociations = toAdd.map((id, i) => ({
    id,
    order: startOrder + i,
    status: "active" as const,
  }));

  const updatedScript = {
    ...script,
    useCaseAssociations: [...(script.useCaseAssociations ?? []), ...newAssociations],
  };

  const response = await putOnboardingPhoneScript(updatedScript, 1);
  return response ?? updatedScript;
}

export function InboundScriptStep({
  onBack,
  onNext,
  newUseCaseIds,
  onUseCaseIdsConsumed,
}: InboundScriptStepProps) {
  const { t } = useTranslation("onboarding");
  const { org, saveOrg } = useOrg();

  const [status, setStatus] = useState<GenerationStatus>({ state: "generating" });
  const [script, setScript] = useState<PhoneScript | null>(null);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const generationStartedRef = useRef(false);

  // Check if this script is already the default
  const [isAlreadyDefault, setIsAlreadyDefault] = useState(false);

  // Stable ref to org so the effect closure always has the latest
  const orgRef = useRef(org);
  orgRef.current = org;

  // Fetch or generate the business-hours script, tracked via onboarding details
  const loadOrGenerate = useCallback(async () => {
    setStatus({ state: "generating" });
    const enforceMinimumLoadingDuration = createMinDurationEnforcer(MIN_BUILDING_SCRIPT_LOADING_MS);
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    try {
      const fetchScriptBundle = async (
        scriptIdToFetch: string
      ): Promise<{ script: PhoneScript | null; useCases: UseCase[] | null }> => {
        const version = scriptApiVersionFromFeatures(orgRef.current?.features);
        const isV2 = version === 2;
        for (let attempt = 0; attempt < SCRIPT_FETCH_RETRY_ATTEMPTS; attempt += 1) {
          const scriptResponse = await apiLive<PhoneScript>(
            getScriptDetailUrl(scriptIdToFetch, version)
          );

          if (scriptResponse) {
            if (shouldSkipUseCaseBatchFetch(isV2, scriptResponse)) {
              return {
                script: scriptResponse,
                useCases: hydrateUseCasesFromScript(scriptResponse),
              };
            }

            const useCasesResponse = await apiLive<UseCase[]>(`${GAIL_API}/v1/use_cases`);
            return {
              script: scriptResponse,
              useCases: useCasesResponse ?? null,
            };
          }

          if (attempt < SCRIPT_FETCH_RETRY_ATTEMPTS - 1) {
            await wait(1000 * Math.pow(2, attempt));
          }
        }

        return { script: null, useCases: null };
      };

      const generateScript = async (): Promise<{
        scriptId: string | null;
        script: PhoneScript | null;
        useCases: UseCase[] | null;
      }> => {
        generationStartedRef.current = true;
        const genResponse = await apiFetch<ScriptGenerationResponse>("/api/onboarding/scripts", "POST");

        if (!genResponse?.success || !genResponse.scriptId) {
          return { scriptId: null, script: null, useCases: null };
        }

        const nextScriptId = genResponse.scriptId;

        // Persist the created script ID in onboarding details (fire-and-forget)
        const latestOrg = orgRef.current;
        saveOrg(
          {
            details: {
              ...latestOrg?.details,
              onboarding: {
                status: latestOrg?.details?.onboarding?.status ?? "in_progress",
                maxCompletedStep: latestOrg?.details?.onboarding?.maxCompletedStep ?? 1,
                ...latestOrg?.details?.onboarding,
                businessHoursScriptId: nextScriptId,
              },
            },
          },
          { silent: true }
        ).catch((err) => {
          console.error("Failed to persist businessHoursScriptId:", err);
        });

        return {
          scriptId: nextScriptId,
          script: genResponse.script ?? null,
          useCases: genResponse.useCases ?? null,
        };
      };

      // Check if we already created a business-hours script in onboarding
      const currentOrg = orgRef.current;
      const existingScriptId = currentOrg?.details?.onboarding?.businessHoursScriptId ?? null;

      let scriptId = existingScriptId;
      let generatedScript: PhoneScript | null = null;
      let generatedUseCases: UseCase[] | null = null;

      // No previously-created script — generate one via the onboarding API
      if (!scriptId && !generationStartedRef.current) {
        const generated = await generateScript();
        scriptId = generated.scriptId;
        generatedScript = generated.script;
        generatedUseCases = generated.useCases;
      }

      if (!scriptId) {
        // Generation is in progress from a concurrent effect run (React strict mode);
        // keep loading — the other invocation will resolve and update state.
        if (generationStartedRef.current) {
          return;
        }
        // Genuinely no script and generation failed
        setStatus({ state: "error" });
        return;
      }

      // If we just generated the script, use the response directly.
      // Otherwise, fetch from the API (returning to a previously-created script).
      let finalScript = generatedScript;
      let finalUseCases = generatedUseCases;

      if (!finalScript) {
        const fetched = await fetchScriptBundle(scriptId);
        finalScript = fetched.script;
        finalUseCases = fetched.useCases;
      }

      // If we still couldn't fetch a script (stale script ID, race condition, etc),
      // regenerate once and retry fetch before failing.
      if (!finalScript) {
        generationStartedRef.current = false;
        const regenerated = await generateScript();
        if (!regenerated.scriptId) {
          await enforceMinimumLoadingDuration();
          setStatus({ state: "error" });
          return;
        }

        scriptId = regenerated.scriptId;
        finalScript = regenerated.script;
        finalUseCases = regenerated.useCases;

        if (!finalScript) {
          const fetched = await fetchScriptBundle(scriptId);
          finalScript = fetched.script;
          finalUseCases = fetched.useCases;
        }

        if (!finalScript) {
          await enforceMinimumLoadingDuration();
          setStatus({ state: "error" });
          return;
        }
      }

      // Check if this script is already the default
      const rulesResponse = await apiLive<InboundRulesSimple>("/api/inbound-rules");
      if (rulesResponse?.defaultScriptId === scriptId) {
        setIsAlreadyDefault(true);
        setSetAsDefault(true);
      }

      // Auto-associate use cases created during directory setup
      if (finalScript && newUseCaseIds && newUseCaseIds.length > 0) {
        finalScript = await associateNewUseCases(
          finalScript,
          newUseCaseIds,
          scriptApiVersionFromFeatures(orgRef.current?.features)
        );
        onUseCaseIdsConsumed?.();
      }

      if (finalScript) {
        setScript(finalScript);
      }

      if (finalUseCases) {
        setUseCases(finalUseCases);
      }

      await enforceMinimumLoadingDuration();
      setStatus({ state: "ready" });
    } catch (error) {
      console.error("Failed to generate inbound script:", error);
      await enforceMinimumLoadingDuration();
      setStatus({ state: "error" });
    }
  }, [newUseCaseIds, onUseCaseIdsConsumed, saveOrg, t]);

  // Run on mount
  useEffect(() => {
    loadOrGenerate();
  }, []);

  const handleRetry = useCallback(() => {
    generationStartedRef.current = false;
    loadOrGenerate();
  }, [loadOrGenerate]);

  const handleScriptUpdate = useCallback((updatedScript: PhoneScript) => {
    setScript(updatedScript);
  }, []);

  const handleUseCasesUpdate = useCallback((updatedUseCases: UseCase[]) => {
    setUseCases(updatedUseCases);
  }, []);

  const handleNext = useCallback(async () => {
    // If user opted to set as default and it's not already default, do it now
    if (setAsDefault && !isAlreadyDefault && script) {
      setIsSavingDefault(true);
      try {
        // Fetch current rules so we preserve existing rules
        const currentRules = await apiLive<InboundRulesSimple>("/api/inbound-rules");

        await apiFetch<InboundRulesSimple>(
          "/api/inbound-rules",
          "PUT",
          {
          body: {
          defaultScriptId: script.id,
          rules: currentRules?.rules ?? [],
        },
        },
        );
      } catch (error) {
        console.error("Failed to set default inbound script:", error);
      } finally {
        setIsSavingDefault(false);
      }
    }
    onNext();
  }, [setAsDefault, isAlreadyDefault, script, onNext]);

  // Get use cases associated with the script
  const scriptUseCases =
    script?.useCaseAssociations
      ?.filter((assoc) => assoc.status === "active")
      .sort((a, b) => a.order - b.order)
      .map((assoc) => useCases.find((uc) => uc.id === assoc.id))
      .filter((uc): uc is UseCase => uc !== undefined) ?? [];

  // Show loading screen while generating
  if (status.state === "generating") {
    return (
      <ScriptBuildingLoader
        title={t("inboundScript.buildingLoader.title")}
        subtitle={t("inboundScript.buildingLoader.subtitle")}
        messages={t("inboundScript.buildingLoader.messages", { returnObjects: true }) as string[]}
        codeLines={SCRIPT_BUILD_WORKSTREAM}
        metricLabels={
          t("inboundScript.buildingLoader.metrics", { returnObjects: true }) as [
            string,
            string,
            string,
          ]
        }
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full max-w-4xl mx-auto space-y-6">
          <OnboardingStepHeader
            title={t("inboundScript.title")}
            description={t("inboundScript.subtitle")}
          />

          {/* Error state */}
          {status.state === "error" && (
            <StepErrorState
              message={t("inboundScript.generationError")}
              onRetry={handleRetry}
              retryLabel={t("inboundScript.retryButton")}
            />
          )}

          {status.state === "ready" && (
            <>
              <ScriptPreviewSection
                script={script}
                useCases={scriptUseCases}
                onScriptUpdate={handleScriptUpdate}
                onUseCasesUpdate={handleUseCasesUpdate}
              />

              {/* Set as default toggle */}
              {script && !isAlreadyDefault && (
                <div
                  className={clsx(
                    "flex items-center justify-between p-4 rounded-xl",
                    "border border-200 bg-panel"
                  )}
                >
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-foreground">
                      {t("inboundScript.setAsDefault")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("inboundScript.setAsDefaultDescription")}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={setAsDefault}
                    onClick={() => setSetAsDefault(!setAsDefault)}
                    className={clsx(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                      setAsDefault ? "bg-primary" : "bg-300"
                    )}
                  >
                    <span
                      className={clsx(
                        "inline-block h-4 w-4 rounded-full bg-white transition-transform",
                        setAsDefault ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>
              )}

              {/* Already default indicator */}
              {script && isAlreadyDefault && (
                <div
                  className={clsx(
                    "flex items-center gap-2 p-4 rounded-xl",
                    "border border-emerald-500/30 bg-emerald-500/5"
                  )}
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    {t("inboundScript.alreadyDefault")}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <StepFooter
        onBack={onBack}
        onSkip={onNext}
        onNext={handleNext}
        nextDisabled={status.state !== "ready"}
        isSaving={isSavingDefault}
        nextContent={
          isSavingDefault ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t("inboundScript.activating")}
            </span>
          ) : undefined
        }
      />
    </div>
  );
}
